/**
 * 用户管理相关API
 */

import type { AxiosRequestConfig } from 'axios'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'
import { request } from '@/shared/api/request'
import type { 
  UserInfo, 
  UserSummary, 
  UserType, 
  UserStatus,
  PaginationParams,
  PaginatedResponse,
  UserGroup
} from '@/api/common-types'

// 重新导出用户类型以保持兼容性
export type { UserInfo, UserSummary, UserGroup }
export type User = UserInfo

// 用户创建请求
export interface UserCreateRequest {
  username: string
  email: string
  password: string
  name: string
  name_remark?: string
  nationality?: string
  language?: string
  language_micro?: string
  school?: string
  department?: string
  phone?: string
  user_type?: UserType
  role_ids?: number[]
  /** 创建用户时必选（表单与后端均校验） */
  group_id?: number
}

// 用户更新请求
export interface UserUpdateRequest {
  name?: string
  name_remark?: string
  nationality?: string
  language?: string
  language_micro?: string
  school?: string
  department?: string
  phone?: string
  user_type?: UserType
  role_ids?: number[]
  group_id?: number
  status?: UserStatus
}

// 用户组创建请求
export interface UserGroupCreateRequest {
  name: string
  description?: string
  group_type?: 'normal' | 'metalingual'
  parent_id?: number | null
  platform_name?: string
}

// 用户组更新请求
export interface UserGroupUpdateRequest {
  name?: string
  description?: string
  group_type?: 'normal' | 'metalingual'
  parent_id?: number | null
  status?: 'active' | 'inactive' | 'suspended'
  platform_name?: string
}

// 用户统计响应
export interface UserStatsResponse {
  total_users: number
  active_users: number
  normal_users: number
  restricted_users: number
  admin_users: number
}

// 用户查询参数
export interface UserQueryParams extends PaginationParams {
  search?: string
  user_type?: UserType
  status?: UserStatus
  group_id?: number
}

/**
 * 获取用户列表
 */
export async function getUsers(
  params?: UserQueryParams,
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<PaginatedResponse<UserInfo>> {
  const response = await request.get<any>('/users', { params, ...config })
  
  // 处理响应数据格式
  if (Array.isArray(response)) {
    return {
      items: response as UserInfo[],
      total: response.length,
      page: params?.page || 1,
      page_size: params?.page_size || params?.limit || 20
    }
  }
  
  if (response && typeof response === 'object') {
    const normalized = response as Record<string, any>
    return {
      items: Array.isArray(normalized.items) ? normalized.items : Array.isArray(normalized.results) ? normalized.results : [],
      total: typeof normalized.total === 'number' ? normalized.total : normalized.items?.length || 0,
      page: normalized.page || params?.page || 1,
      page_size: normalized.page_size || normalized.limit || params?.page_size || params?.limit || 20
    }
  }
  
  return { 
    items: [], 
    total: 0,
    page: params?.page || 1,
    page_size: params?.page_size || params?.limit || 20
  }
}

/**
 * 获取用户详情
 */
export async function getUser(
  id: number,
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<UserInfo> {
  return await request.get<UserInfo>(`/users/${id}`, config)
}

/** 用户角色项（与 GET /users/:id/roles 返回一致） */
export interface UserRoleItem {
  id: number
  name: string
  code: string
  description?: string
  is_system?: boolean
  is_default?: boolean
  status: string
}

/**
 * 获取用户角色列表
 */
export async function getUserRoles(userId: number): Promise<UserRoleItem[]> {
  return await request.get<UserRoleItem[]>(`/users/${userId}/roles`)
}

/**
 * 获取当前用户信息
 */
export async function getCurrentUserProfile(
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<UserInfo> {
  return await request.get<UserInfo>('/users/me', config)
}

/**
 * 创建用户
 */
export async function createUser(data: UserCreateRequest): Promise<UserInfo> {
  return await request.post<UserInfo>('/users', data)
}

/**
 * 更新用户
 */
export async function updateUser(id: number, data: UserUpdateRequest): Promise<UserInfo> {
  return await request.put<UserInfo>(`/users/${id}`, data)
}

/**
 * 删除用户
 */
export async function deleteUser(id: number): Promise<void> {
  await request.delete(`/users/${id}`)
}

/**
 * 获取用户组列表
 */
export async function getUserGroups(
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<UserGroup[]> {
  return await request.get<UserGroup[]>('/users/groups', config)
}

/**
 * 获取用户组树形结构
 */
export async function getUserGroupTree(): Promise<UserGroup[]> {
  return await request.get<UserGroup[]>('/users/groups/tree')
}

/**
 * 获取单个用户组详情
 */
export async function getUserGroup(id: number): Promise<UserGroup> {
  return await request.get<UserGroup>(`/users/groups/${id}`)
}

/**
 * 获取用户组的用户列表
 */
export async function getGroupUsers(groupId: number): Promise<UserInfo[]> {
  return await request.get<UserInfo[]>(`/users/groups/${groupId}/users`)
}

/**
 * 创建用户组
 */
export async function createUserGroup(data: UserGroupCreateRequest): Promise<UserGroup> {
  return await request.post<UserGroup>('/users/groups', data)
}

/**
 * 更新用户组
 */
export async function updateUserGroup(id: number, data: UserGroupUpdateRequest): Promise<UserGroup> {
  return await request.put<UserGroup>(`/users/groups/${id}`, data)
}

/**
 * 删除用户组
 */
export async function deleteUserGroup(id: number): Promise<void> {
  await request.delete(`/users/groups/${id}`)
}

/**
 * 移动用户组（更改父组）
 */
export async function moveUserGroup(groupId: number, parentId?: number | null): Promise<UserGroup> {
  return await request.put<UserGroup>(`/users/groups/${groupId}/move`, { parent_id: parentId })
}

/**
 * 上传用户组Logo
 */
export async function uploadGroupLogo(groupId: number, file: File): Promise<UserGroup> {
  const formData = new FormData()
  formData.append('logo', file)
  return await request.post<UserGroup>(`/users/groups/${groupId}/logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 获取用户统计信息
 */
export async function getUserStats(
  config?: AxiosRequestConfig | BatchRequestConfig,
): Promise<UserStatsResponse> {
  return await request.get<UserStatsResponse>('/users/stats', config)
}

/**
 * 重置用户密码
 */
export async function resetUserPassword(id: number, newPassword: string): Promise<void> {
  await request.post(`/users/${id}/reset-password`, { new_password: newPassword })
}

// 导出API对象（兼容旧代码）
export const userApi = {
  getUsers,
  getUser,
  getUserRoles,
  getCurrentUserProfile,
  createUser,
  updateUser,
  deleteUser,
  getUserGroups,
  getUserGroupTree,
  getUserGroup,
  getGroupUsers,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
  moveUserGroup,
  uploadGroupLogo,
  getUserStats,
  resetUserPassword
}

